// The city view's camera (pinch-to-zoom, Oct 9 2026 — plan: claude/valor-web-city-zoom-plan.md). Pure maths, no DOM:
// the world (the Hearthstone Refined city's frame since Oct 12 2026: 1181 x 1343 px, cityScene.SCENE) is drawn at scale
// `s` and offset (`x`, `y`) inside a viewport of `vw` x `vh` CSS px. Zoom is talked about as `rel` = s / fit, where fit
// is the "whole city" scale (today's view = 1x).
//
//   0.5x  Overview — the walled city in its countryside (no labels: Oct 10 2026)
//   1x    the whole city (fits the width on a phone; on a wide screen it fits the height, never narrower than a phone)
//   2.5x  the closest zoom

export interface Cam {
  s: number;
  x: number;
  y: number;
}

export interface View {
  /** viewport, CSS px */
  vw: number;
  vh: number;
  /** world size, frame px (cityScene.SCENE.W / H) */
  W: number;
  H: number;
  /** how far the camera may pan side to side, frame px (cityScene.SCENE.wallX0/X1: the frame, walls inside it) */
  X0: number;
  X1: number;
  /** zoom range, relative to fit */
  minRel: number;
  maxRel: number;
}

/** The farthest the city zooms out (Oct 13 2026: 0.8x, was 0.5x — the countryside surround shows around it). */
export const OVERVIEW_REL = 0.8;
/** Zoom out past the overview to here (the fingers' zoom, before the rubber band) and letting go opens the world map.
 *  The same pinch as before: 0.6 of the overview (it was 0.3 of 0.5). */
export const MAP_REL = OVERVIEW_REL * 0.6;
/** The overview's zoom thresholds were laid out for a 0.5x overview; this keeps their spacing between the overview and
 *  1x (0.5 → OVERVIEW_REL, 1 → 1). */
const nearOverview = (x: number) => OVERVIEW_REL + ((x - 0.5) * (1 - OVERVIEW_REL)) / 0.5;
const CENTRE_FROM = nearOverview(0.75);
const CENTRE_AT = nearOverview(0.56);
const FAR_BELOW = nearOverview(0.8);
const CALLOUTS_FROM = nearOverview(0.72);
const CALLOUTS_AT = nearOverview(0.56);
export const MAX_REL = 2.5;
/** The camera is "at the whole city" within this much of 1x (the whole-city button hides). */
export const WHOLE_EPS = 0.04;

export const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

/** Scale at 1x: the city's width = clamp(min(vw, 480), what fits the height, vw). On a phone that is the width (as
 *  before); on a tablet or PC the city fits the height, but never narrower than a phone. */
export function fitS(v: Pick<View, "vw" | "vh" | "W" | "H">): number {
  const wide = clamp((v.vh * v.W) / v.H, Math.min(v.vw, 480), v.vw);
  return Math.max(1e-6, wide / v.W);
}

export const minS = (v: View) => fitS(v) * v.minRel;
export const maxS = (v: View) => fitS(v) * v.maxRel;
export const relOf = (v: View, c: Cam) => c.s / fitS(v);

export interface Bounds {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

/** Where the city may sit at scale s. Side to side the walled city (X0..X1, the wall's outer faces) counts; top to bottom
 *  the city (0..H). Bigger than the window: it must cover the window. Smaller: it stays fully on screen and can slide inside
 *  it — except side to side near the overview, where it eases to centred (CENTRE_FROM → CENTRE_AT). */
export function bounds(v: View, s: number): Bounds {
  const cw = (v.X1 - v.X0) * s;
  const ch = v.H * s;
  const f = clamp((s / fitS(v) - CENTRE_AT) / (CENTRE_FROM - CENTRE_AT), 0, 1);
  const xs =
    cw < v.vw
      ? { x0: ((v.vw - cw) / 2) * (1 - f) - v.X0 * s, x1: ((v.vw - cw) / 2) * (1 + f) - v.X0 * s }
      : { x0: v.vw - v.X1 * s, x1: -v.X0 * s };
  return { x0: xs.x0, x1: xs.x1, y0: Math.min(0, v.vh - ch), y1: Math.max(0, v.vh - ch) };
}

/** Past an edge the city moves at a third of the finger's speed. */
export function rubber(val: number, lo: number, hi: number): number {
  if (val < lo) return lo - (lo - val) * 0.33;
  if (val > hi) return hi + (val - hi) * 0.33;
  return val;
}

/** Past the zoom range a pinch keeps going, slowly (it springs back on release). */
export function rubberScale(s: number, lo: number, hi: number): number {
  if (s > hi) return hi * Math.pow(s / hi, 0.3);
  if (s < lo) return lo * Math.pow(s / lo, 0.3);
  return s;
}

/** How far a zoom-out past the overview has pulled towards the world map: 0 at the overview, 1 at MAP_REL (let go → the
 *  map). `rawRel` is the zoom the fingers ask for, before the rubber band holds the city at the overview. */
export function mapPull(rawRel: number, minRel: number = OVERVIEW_REL): number {
  if (minRel > OVERVIEW_REL + 1e-9) return 0;
  return clamp((OVERVIEW_REL - rawRel) / (OVERVIEW_REL - MAP_REL), 0, 1);
}

/** The same pull for the mouse wheel / trackpad at the overview: wheel-out steps add up (in log-zoom, the units wheelZoom
 *  uses; they reset after a pause) until about five mouse-wheel notches, so one flick at the overview doesn't leave the
 *  city. */
export const MAP_PULL_LOG = 1;
export const wheelLog = (deltaY: number, ctrl: boolean) => deltaY * (ctrl ? 0.012 : 0.0022);

/** The nearest legal camera. */
export function clampCam(v: View, c: Cam): Cam {
  const s = clamp(c.s, minS(v), maxS(v));
  const b = bounds(v, s);
  return { s, x: clamp(c.x, b.x0, b.x1), y: clamp(c.y, b.y0, b.y1) };
}

/** Scale to s keeping the world point under (px, py) where it is. */
export function aboutPoint(from: Cam, s: number, px: number, py: number): Cam {
  const wx = (px - from.x) / from.s;
  const wy = (py - from.y) / from.s;
  return { s, x: px - wx * s, y: py - wy * s };
}

/** The camera at zoom rel with world point (cx, cy) in the middle of the window (clamped). */
export function centreOn(v: View, rel: number, cx: number, cy: number): Cam {
  const s = fitS(v) * rel;
  return clampCam(v, { s, x: v.vw / 2 - cx * s, y: v.vh / 2 - cy * s });
}

export const whole = (v: View) => centreOn(v, 1, v.W / 2, v.H / 2);
export const overview = (v: View) => centreOn(v, Math.max(v.minRel, OVERVIEW_REL), v.W / 2, v.H / 2);

/** The world point in the middle of the window. */
export function centreOf(c: Cam, vw: number, vh: number): { x: number; y: number } {
  return { x: (vw / 2 - c.x) / c.s, y: (vh / 2 - c.y) / c.s };
}

/** Double-tap / double-click on open ground: zoom in 1.8x about that point; at the closest zoom, back to the whole city. */
export function stepZoom(v: View, c: Cam, px: number, py: number): Cam {
  if (c.s >= maxS(v) * 0.98) return whole(v);
  return clampCam(v, aboutPoint(c, Math.min(maxS(v), c.s * 1.8), px, py));
}

/** Keys + / -: 1.4x steps about the middle of the window. */
export function keyZoom(v: View, c: Cam, dir: 1 | -1): Cam {
  const s = dir > 0 ? Math.min(maxS(v), c.s * 1.4) : Math.max(minS(v), c.s / 1.4);
  return clampCam(v, aboutPoint(c, s, v.vw / 2, v.vh / 2));
}

/** Mouse wheel / trackpad: ctrl+wheel is a trackpad pinch (finer deltas, so a bigger factor). */
export function wheelZoom(v: View, c: Cam, deltaY: number, ctrl: boolean, px: number, py: number): Cam {
  const k = Math.exp(-wheelLog(deltaY, ctrl));
  return clampCam(v, aboutPoint(c, clamp(c.s * k, minS(v), maxS(v)), px, py));
}

/** Construction mode: where to move the camera so a tapped building sits low enough for its pop-up (popH px tall) to
 *  open above it, at 1.5x or closer. `null` when it already fits where it is. */
export function focusFor(v: View, c: Cam, ax: number, ay: number, popH: number): Cam | null {
  const fit = fitS(v);
  const sx = ax * c.s + c.x;
  const sy = ay * c.s + c.y;
  const ph = popH + 24;
  const fits = c.s >= fit * 1.2 && sy - ph > 50 && sx > 120 && sx < v.vw - 120 && sy < v.vh - 40;
  if (fits) return null;
  const s = Math.max(c.s, Math.min(maxS(v), fit * 1.5));
  return clampCam(v, { s, x: v.vw / 2 - ax * s, y: Math.max(ph + 60, v.vh * 0.55) - ay * s });
}

// ------------------------------------------------------------------ labels by zoom

/** On-building labels at the whole-city view (Settings › Display › City labels). */
export type LabelPref = "full" | "compact" | "level";
/** full: name + status pill · compact: name (urgent pills stay) · level: the level disc only · far: discs, urgent
 *  pills as icons, Wheel and Loyalty step back (zoomed out past FAR_BELOW, until the callouts take over). */
export type Lod = "full" | "compact" | "level" | "far";

export function lodOf(rel: number, pref: LabelPref): Lod {
  if (rel < FAR_BELOW) return "far";
  if (rel >= 1.35) return "full";
  return pref;
}

const smooth = (t: number) => t * t * (3 - 2 * t);

/** How far the overview has taken over from the on-building plates: 0 at CALLOUTS_FROM and closer, 1 at CALLOUTS_AT. */
export function overviewT(rel: number): number {
  return smooth(clamp((CALLOUTS_FROM - rel) / (CALLOUTS_FROM - CALLOUTS_AT), 0, 1));
}

/** A drag's release speed (px/ms) from its last few samples. */
export function releaseVelocity(hist: { t: number; x: number; y: number }[], now: number, x: number, y: number): { vx: number; vy: number } {
  if (!hist.length) return { vx: 0, vy: 0 };
  const old = hist.find((q) => now - q.t < 90) ?? hist[0];
  const dt = Math.max(16, now - old.t);
  return { vx: (x - old.x) / dt, vy: (y - old.y) / dt };
}

/** One momentum step (dt ms): moves, decays, and brakes harder past an edge. */
export function glideStep(v: View, c: Cam, vel: { vx: number; vy: number }, dt: number): { cam: Cam; vx: number; vy: number; done: boolean } {
  const b = bounds(v, c.s);
  const x = c.x + vel.vx * dt;
  const y = c.y + vel.vy * dt;
  const decay = Math.pow(0.94, dt / 16);
  let vx = vel.vx * decay;
  let vy = vel.vy * decay;
  if (x < b.x0 || x > b.x1) vx *= 0.6;
  if (y < b.y0 || y > b.y1) vy *= 0.6;
  return { cam: { s: c.s, x, y }, vx, vy, done: Math.hypot(vx, vy) < 0.03 };
}

/** Ease-out cubic between two cameras. */
export function lerpCam(a: Cam, b: Cam, k: number): Cam {
  const e = 1 - Math.pow(1 - clamp(k, 0, 1), 3);
  return { s: a.s + (b.s - a.s) * e, x: a.x + (b.x - a.x) * e, y: a.y + (b.y - a.y) * e };
}

// ------------------------------------------------------------------ the saved view

export type OpensAt = "last" | "overview" | "whole" | "close";
/** The saved view's format. 2 = world centre in the Hearthstone Refined frame (Oct 12 2026); the unversioned views saved
 *  before it held a centre in the old 1054 x 1472 painting, which means nothing in the new frame. */
export const CITY_VIEW_VERSION = 2;
export interface SavedView {
  v?: number;
  rel: number;
  cx: number;
  cy: number;
}

/** A saved view from storage, or null when there is none or it was saved for another frame (an old, unversioned one) or
 *  is malformed — then it should be dropped, once (`stale`). */
export function parseSavedView(raw: string | null): { view: SavedView | null; stale: boolean } {
  if (!raw) return { view: null, stale: false };
  try {
    const v = JSON.parse(raw) as Partial<SavedView> | null;
    const ok = !!v && v.v === CITY_VIEW_VERSION && Number.isFinite(v.rel) && Number.isFinite(v.cx) && Number.isFinite(v.cy);
    return ok ? { view: { v: CITY_VIEW_VERSION, rel: v!.rel!, cx: v!.cx!, cy: v!.cy! }, stale: false } : { view: null, stale: true };
  } catch {
    return { view: null, stale: true };
  }
}

/** Where the city view opens (Settings › Display › City view opens at). Close-up looks at the heart of the city
 *  (`close`, a world point: cityScene.SCENE.close). */
export function openingCam(v: View, opens: OpensAt, saved: SavedView | null, close: { x: number; y: number } = { x: v.W / 2, y: v.H / 2 }): Cam {
  switch (opens) {
    case "overview": return overview(v);
    case "close": return centreOn(v, 1.6, close.x, close.y);
    case "last":
      if (saved && Number.isFinite(saved.rel) && Number.isFinite(saved.cx) && Number.isFinite(saved.cy)) {
        return centreOn(v, clamp(saved.rel, v.minRel, v.maxRel), saved.cx, saved.cy);
      }
      return whole(v);
    default: return whole(v);
  }
}

/** The view to save (world centre + zoom), so a different window size reopens on the same spot. */
export function saveOf(v: View, c: Cam): SavedView {
  const m = centreOf(c, v.vw, v.vh);
  return { v: CITY_VIEW_VERSION, rel: relOf(v, c), cx: m.x, cy: m.y };
}
